import Route from '@ember/routing/route'
import type RouterService from '@ember/routing/router-service'
import { service } from '@ember/service'
import type ContentfulClientService from 'teacher-ui-client/services/contentful-client'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type { TeacherResourceCategoryEntry } from 'teacher-ui-client/contentful-client/entry-types'
import type { Log } from '@blakeelearning/log'
import { assert } from '@ember/debug'

export default class extends Route {
  @service contentfulClient!: ContentfulClientService

  @service router!: RouterService

  @service log!: Log

  teacherResourceCategories: Partial<Record<SubscriptionType, TeacherResourceCategoryEntry>> = {}

  queryParams = {
    category: {
      refreshModel: true,
    },
  }

  cacheCategories(subscriptionType: SubscriptionType, teacherResourceCategories?: TeacherResourceCategoryEntry) {
    if (typeof subscriptionType === 'string' && !this.teacherResourceCategories[subscriptionType]) {
      this.teacherResourceCategories[subscriptionType] = teacherResourceCategories
    }
    return teacherResourceCategories
  }

  peekCategories(subscriptionType: SubscriptionType) {
    if (typeof subscriptionType === 'string' && this.teacherResourceCategories[subscriptionType]) {
      return this.teacherResourceCategories[subscriptionType]
    }
  }

  async fetchCategories(subscriptionType: SubscriptionType) {
    assert(
      'only reading or maths',
      subscriptionType === SubscriptionType.Reading || subscriptionType === SubscriptionType.Maths,
    )
    const teacherResourceCategories =
      this.peekCategories(subscriptionType) ||
      (await this.contentfulClient.getTeacherResourceCategories({ subscriptionType }))
    this.cacheCategories(subscriptionType, teacherResourceCategories)
    return teacherResourceCategories
  }

  async model(params: { category?: string }) {
    const { subscriptionType } = this.modelFor('subscription-type') as ModelForSubscriptionTypeRoute

    const rootCategoryEntry = await this.fetchCategories(subscriptionType)

    // This should not happen.
    if (!rootCategoryEntry) {
      this.log.error(
        `Failed to retrieve root TeacherResourceCategory for subscriptionType:${subscriptionType} from Contentful. Check that the entry exists and is published.`,
      )
      void this.router.replaceWith('subscription-type.home')
      return { categoriesForQuery: [], selectedCategory: undefined }
    }

    if (!rootCategoryEntry.fields.path) {
      this.log.error(
        `A root TeacherResourceCategory must have a value for it's path field. Check Contentful entry TeacherResourceCategory:${rootCategoryEntry.sys.id}`,
      )
      void this.router.replaceWith('subscription-type.home')
      return { categoriesForQuery: [], selectedCategory: undefined }
    }

    // slash-separated query path from the url e.g '?category=/reading/lower'
    const query = params.category ?? rootCategoryEntry.fields.path
    const categoriesForQuery = this.categoriesForQuery(query, rootCategoryEntry)
    const selectedCategory = categoriesForQuery.at(-1)

    return {
      categoriesForQuery,
      selectedCategory,
    }
  }

  /**
   * Build an array of category entries matching the query, from most-specific to least-specific
   */
  categoriesForQuery(query: string, category: TeacherResourceCategoryEntry): TeacherResourceCategoryEntry[] {
    if (query === category.fields.path) {
      // exact match, must be last entry in the path
      return [category]
    }
    if (category.fields.path && query.startsWith(category.fields.path)) {
      // partial match, must have child entries to fill out the path
      return [
        category,
        ...(category.fields.categories?.flatMap((entry) => (entry ? this.categoriesForQuery(query, entry) : [])) ?? []),
      ]
    }
    // no match, category not in the path
    return []
  }
}
